import { IconButton } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import ToolbarTitle from "design/components/texts/ToolbarTitle";
import ToolbarContainer from "design/components/toolbar/ToolbarContainer";
import Right from "design/layouts/Right";
import SplitView from "design/layouts/SplitView";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import { getFileApiURL } from "qpr-base/utils/url-utils";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  useDashboard,
  useDashboardMutations,
} from "../../MyDashboards/api/useDashboards.hook";
import ExportDashboardPopUp from "../../MyDashboards/components/ExportDashboard/ExportDashboardPopup/ExportDashboardPopup";
import { FileTypes } from "../../MyDashboards/components/ExportDashboard/ExportDashboardTypes";
import StartDashboardExport from "../../MyDashboards/components/ExportDashboard/StartDashboardExport";
import useShowToast from "../../MyDashboards/components/QPRToast/useShowToast";
import SaveModal from "../../MyDashboards/components/SaveModal";
import useDashboardEditor from "../../MyDashboards/state/editor";
import { EditorMode } from "../../MyDashboards/state/editorTypes";
import { DashboardOpenMode } from "../../MyDashboards/state/types";
import scaleDashboardPages from "../../MyDashboards/utils/scaleDashboard";
import ActionButton from "../../design/components/buttons/ToolbarBtn";

const useIconStyles = makeThemedStyles(
  "CreateDashboardToolbarIcon",
  (theme) => ({
    root: {
      marginLeft: 0,
      marginRight: "15px",
      marginTop: 0,
      width: "40px",
      height: "40px",
      backgroundImage: `url(${getFileApiURL(
        `/icons/navitems/icon_my_dashboards.svg`
      )})`,
    },
  })
);

const useStyles = makeThemedStyles("CreateDashboardToolbar", (theme) => ({
  rightSide: {
    display: "flex",
    alignItems: "center",
    marginTop: "-4px",
    marginRight: "30px",
    gap: "10px",
  },
  exportDashboard: {
    textAlign: "center",
    borderRadius: "30px",
    fontFamily: "Dubai",
    borderWidth: "0px",
    fontSize: "14px",
    fontWeight: 400,
    minWidth: "160px",
    lineHeight: "20px",
    letterSpacing: "0.04375px",
    padding: "0px 20px 0px 20px",
    cursor: "pointer",
    marginTop: 0,
    height: "36px",
    textTransform: "none",
    background: "rgb(248,248,248)",
    marginInlineEnd: "5px",
    "&:hover": {
      backgroundColor: "rgb(238, 238, 243)",
    },
  },
  button: {},
  plusSign: {},
}));

const useBackIconStyles = makeThemedStyles(
  "Navigation.BackButton",
  (theme) => ({
    root: {},
    Button: {
      background: "rgb(248,248,248)",
      "&:hover": {
        backgroundColor: "rgb(238, 238, 243)",
      },
      width: "30px",
      height: "30px",
      marginLeft: "20px",
      padding: 0,
    },
  })
);

const BackButton = (props: { historyBack?: boolean }) => {
  const backIconClasses = useBackIconStyles();
  const history = useHistory();
  const rtl = useAppContext((s) => s.rtl);
  return (
    <IconButton
      className={backIconClasses.Button}
      style={{ marginLeft: rtl ? "16px" : "", marginRight: rtl ? "26px" : "" }}
      onClick={() => history.go(-1)}
    >
      {rtl ? (
        <ChevronRight classes={backIconClasses} fontSize="large" />
      ) : (
        <ChevronLeft classes={backIconClasses} fontSize="large" />
      )}
    </IconButton>
  );
};

const CreateDashboardToolbar = (props: { mode?: DashboardOpenMode }) => {
  const { mode } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const iconstyles = useIconStyles();

  const ctx = useAppContext();
  const pages = useDashboardEditor((d) => d.pages);
  const mutations = useDashboardMutations();
  const setUnsaved = useDashboardEditor((e) => e.setUnsavedChanges);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const editor = useDashboardEditor();
  const setEditMode = useDashboardEditor((e) => e.setEditorMode);
  const setActiveElement = useDashboardEditor((s) => s.setSelectedElement);
  const editMode = useDashboardEditor((e) => e.mode); // is affected by edit/preivew toggle switch
  const [isExporting, setIsExporting] = React.useState(false);
  const link = useLink({ noPeriod: true });
  const showToast = useShowToast();
  const [fileType, setFileType] = React.useState<FileTypes>("pdf");

  const { isLoading, data: currentDashboard } = useDashboard(ctx.elementId);
  const [isSaveModalOpen, setIsSaveModalOpen] = React.useState(false);

  const title = () => {
    if (mode === DashboardOpenMode.create)
      return t("myStories.Create Dashboard");
    if (editMode === EditorMode.preview)
      return `${t("myStories.View Dashboard")}: ${
        currentDashboard?.name || "..."
      }`;
    if (editMode === EditorMode.edit)
      return `${t("myStories.Edit Dashboard")}: ${
        currentDashboard?.name || "..."
      }`;

    return t("myStories.Error");
  };

  React.useEffect(() => {
    if (mutations.add.isSuccess) {
      setIsSaveModalOpen(false);
      link({
        page: "editslidetemplatedashboard",
        meaId: mutations?.add?.data?._id,
      });
      setTimeout(
        () => showToast("success", t("notifications.dashboardCreatedSuccess")),
        0
      );
    } else if (mutations.add.isError) {
      showToast("fail", t("notifications.dashboardCreatedFailed"));
    }
  }, [mutations.add.isLoading]);

  React.useEffect(() => {
    if (mode === DashboardOpenMode.create) {
      setEditMode(EditorMode.edit);
    }
  }, [mode]);

  React.useEffect(() => {
    if (mutations.update.isSuccess) {
      setIsSaveModalOpen(false);
      showToast("success", t("notifications.dashboardEditSuccess"));
    } else if (mutations.update.isError) {
      showToast("fail", t("notifications.dashboardEditedFailed"));
    }
  }, [mutations.update.isLoading]);

  const handleSelectFile = (type: FileTypes) => {
    setFileType(type);
    setIsExporting(true);
  };

  return (
    <>
      <ToolbarContainer>
        <BackButton />
        <SplitView partCnt={2}>
          <ToolbarTitle>{title()}</ToolbarTitle>
          <Right>
            <div className={classes.rightSide}>
              {editMode !== EditorMode.preview && (
                <ActionButton
                  text={t(
                    mode === DashboardOpenMode.create
                      ? t("buttons.Create")
                      : t("buttons.Save")
                  )}
                  onClick={() => {
                    setActiveElement(undefined);
                    setIsSaveModalOpen(true);
                  }}
                  color="grayBg"
                />
              )}

              {mode === DashboardOpenMode.view ||
              (mode === DashboardOpenMode.edit && pages && pages.length > 0) ? (
                <Button
                  className={`${classes.exportDashboard} exportBtn`}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    setAnchorEl(event.currentTarget);
                  }}
                >
                  {t("myStories.Export Story")}
                </Button>
              ) : null}
            </div>
          </Right>
        </SplitView>
      </ToolbarContainer>
      {isExporting ? (
        <StartDashboardExport
          onClose={() => {
            setIsExporting(false);
          }}
          fileType={fileType}
        />
      ) : null}
      <ExportDashboardPopUp
        handleClick={handleSelectFile}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
      />
      {!isLoading && (
        <SaveModal
          initialValue={currentDashboard?.name}
          fieldTitle={t("myStories.Story name")}
          modalTitle={t(
            mode === DashboardOpenMode.create
              ? t("myStories.Create Dashboard")
              : t("myStories.Save Dashboard")
          )}
          actionName={
            mode === DashboardOpenMode.create
              ? t("buttons.Create")
              : t("buttons.Save")
          }
          open={isSaveModalOpen}
          showLoadingIndicator={
            mutations.add.isLoading || mutations.update.isLoading
          }
          onAccept={(newTitle) => {
            setUnsaved(false);
            if (ctx.elementId) {
              window.localStorage.removeItem(ctx.elementId);
              // should update without changing or asking for name. However, their should be a rename option somewhere
              mutations.update.mutate({
                _id: ctx.elementId,
                name: newTitle,
                dashboardData: {
                  pptFile: `/report/templates/report_slide_templates_${
                    ctx.language === "ar" ? "ar" : "en"
                  }.pptx`,
                  pages: scaleDashboardPages(
                    1 / editor.scaleFactor,
                    editor.pages
                  ),
                },
              });
            } else {
              mutations.add.mutate({
                name: newTitle,
                languageName: ctx.language,
                useCaseName: "Year Report",
                dashboardData: {
                  pptFile: `/report/templates/report_slide_templates_${
                    ctx.language === "ar" ? "ar" : "en"
                  }.pptx`,
                  pages: scaleDashboardPages(
                    1 / editor.scaleFactor,
                    editor.pages
                  ),
                },
              });
            }
          }}
          onClose={() => {
            setIsSaveModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default CreateDashboardToolbar;
